import {
  DrinkingStatus,
  Ethnicity,
  OpenTo,
  SmokerStatus,
  TattooStatus,
} from "@/generated-api";
import { z } from "zod";
import { MAX_HEIGHT, MIN_HEIGHT } from "./HeightSelector";
import { TranslationTree } from "@/translations";

export const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

export const optionWithIdSchema = z.object({
  label: z.string(),
  value: z.string(),
  id: z.string(),
});

export const createAlwaysRequiredDetailsFormSchema = (t: TranslationTree) =>
  z.object({
    displayName: z
      .string()
      .min(3, t.USER_FORM_SCHEMA_VALIDATION.DISPLAY_NAME.MIN)
      .max(15, t.USER_FORM_SCHEMA_VALIDATION.DISPLAY_NAME.MAX)
      .regex(
        /^[a-zA-Z0-9.]+$/,
        t.USER_FORM_SCHEMA_VALIDATION.DISPLAY_NAME.REGEX
      ),
    mainLocation: z
      .array(optionSchema)
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.CITY.MIN)
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.CITY.MAX),
    dob: z.date(),
    languages: z
      .array(optionWithIdSchema)
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.LANGUAGES.MIN)
      .max(5, t.USER_FORM_SCHEMA_VALIDATION.LANGUAGES.MAX),
    nationality: z
      .array(optionWithIdSchema)
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.NATIONALITY.MIN)
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.NATIONALITY.MAX),
    ethnicity: z.nativeEnum(Ethnicity),
    openTo: z
      .array(z.nativeEnum(OpenTo))
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.OPEN_TO.MIN),
  });

export const createWomenRequiredDetailsFormSchema = (t: TranslationTree) =>
  z.object({
    height: z.number().min(MIN_HEIGHT).max(MAX_HEIGHT),
    smoker: z
      .array(z.nativeEnum(SmokerStatus))
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MIN)
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MAX),
    tattoos: z
      .array(z.nativeEnum(TattooStatus))
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MIN)
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MAX),
    drinking: z
      .array(z.nativeEnum(DrinkingStatus))
      .min(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MIN)
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MAX),
    bio: z
      .string()
      .min(30, t.USER_FORM_SCHEMA_VALIDATION.BIO.MIN)
      .max(250, t.USER_FORM_SCHEMA_VALIDATION.BIO.MAX),
  });

export const createMenOptionalDetailsFormSchema = (t: TranslationTree) =>
  z.object({
    dob: z.date(),
    height: z.number().min(MIN_HEIGHT).max(MAX_HEIGHT).optional(),
    smoker: z
      .array(z.nativeEnum(SmokerStatus))
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MAX)
      .optional(),
    tattoos: z
      .array(z.nativeEnum(TattooStatus))
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MAX)
      .optional(),
    drinking: z
      .array(z.nativeEnum(DrinkingStatus))
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.BADGES.MAX)
      .optional(),
    bio: z
      .union([
        z
          .string()
          .min(30, t.USER_FORM_SCHEMA_VALIDATION.BIO.MIN)
          .max(250, t.USER_FORM_SCHEMA_VALIDATION.BIO.MAX),
        z.string().length(0),
      ])
      .optional(),
  });

export const createOptionalDetailsFormSchema = (t: TranslationTree) =>
  z.object({
    secondaryLocation: z
      .array(optionSchema)
      .max(1, t.USER_FORM_SCHEMA_VALIDATION.CITY.MAX)
      .optional(),
    referredBy: z
      .union([
        z
          .string()
          .min(3, t.USER_FORM_SCHEMA_VALIDATION.REFERRED_BY.MIN)
          .max(100, t.USER_FORM_SCHEMA_VALIDATION.REFERRED_BY.MAX)
          .regex(
            /^[a-zA-Z0-9._%+-@]+$/,
            t.USER_FORM_SCHEMA_VALIDATION.REFERRED_BY.REGEX
          ),
        z.string().length(0),
      ])
      .optional(),
    telegram: z
      .union([
        z
          .string()
          .min(3, t.USER_FORM_SCHEMA_VALIDATION.TELEGRAM.MIN)
          .max(40, t.USER_FORM_SCHEMA_VALIDATION.TELEGRAM.MAX)
          .regex(
            /^[a-zA-Z0-9_]+$/,
            t.USER_FORM_SCHEMA_VALIDATION.TELEGRAM.REGEX
          ),
        z.string().length(0),
      ])
      .optional(),
    instagram: z
      .union([
        z
          .string()
          .min(1, t.USER_FORM_SCHEMA_VALIDATION.INSTAGRAM.MIN)
          .max(30, t.USER_FORM_SCHEMA_VALIDATION.INSTAGRAM.MAX)
          .regex(
            /^[a-zA-Z0-9._]+$/,
            t.USER_FORM_SCHEMA_VALIDATION.INSTAGRAM.REGEX
          )
          .regex(
            /^(?!.*\.\.).*$/,
            t.USER_FORM_SCHEMA_VALIDATION.INSTAGRAM.DUPLICATE_PERIODS
          )
          .regex(
            /^(?![.]).*$/,
            t.USER_FORM_SCHEMA_VALIDATION.INSTAGRAM.START_PERIODS
          )
          .regex(
            /.*[^.]$/,
            t.USER_FORM_SCHEMA_VALIDATION.INSTAGRAM.END_PERIODS
          ),
        z.string().length(0),
      ])
      .optional(),
    whatsapp: z
      .union([
        z
          .string()
          .min(6, t.USER_FORM_SCHEMA_VALIDATION.WHATSAPP.MIN)
          .max(20, t.USER_FORM_SCHEMA_VALIDATION.WHATSAPP.MAX)
          .regex(/^\+?[0-9]+$/, t.USER_FORM_SCHEMA_VALIDATION.WHATSAPP.REGEX),
        z.string().length(0),
      ])
      .optional(),
  });
